import { X, Trash2, AlertTriangle, ShieldAlert } from "lucide-react";

/**
 * Detail / management modal for a single user — opened from the active users
 * table. Renders the user's stats and exposes Soft-Delete / Hard-Delete
 * actions, with all safety rails (self, protected, direktor→direktor/admin).
 */
export const UserManageModal = ({
  user,
  currentUser,
  onClose,
  onSoftDelete,
  onRequestHardDelete,
}) => {
  if (!user) return null;
  const isSelf = currentUser?.id === user.id;
  const isProtected = !!user.is_protected;
  const directorBlocked =
    currentUser?.role === "direktor" && ["direktor", "admin"].includes(user.role);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="user-manage-modal"
    >
      <div
        className="nv-card w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <span className="label-eyebrow">Nutzer-Verwaltung</span>
            <h3 className="font-display font-black text-2xl text-white mt-1">
              {user.first_name} {user.last_name}
            </h3>
            <p className="text-xs text-novatus-text-3 mt-1 font-mono">{user.email}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/5 rounded-md text-novatus-text-2" data-testid="user-manage-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="bg-novatus-card-2 border border-novatus rounded-lg p-3">
            <div className="label-eyebrow">Rolle</div>
            <div className="text-white mt-1 font-semibold capitalize">{user.role}</div>
          </div>
          <div className="bg-novatus-card-2 border border-novatus rounded-lg p-3">
            <div className="label-eyebrow">Level</div>
            <div className="text-white mt-1 font-semibold">{user.level || "—"}</div>
          </div>
          <div className="bg-novatus-card-2 border border-novatus rounded-lg p-3">
            <div className="label-eyebrow">XP</div>
            <div className="font-mono text-white mt-1">{user.xp || 0}</div>
          </div>
          <div className="bg-novatus-card-2 border border-novatus rounded-lg p-3">
            <div className="label-eyebrow">Trainings</div>
            <div className="font-mono text-white mt-1">{user.total_sessions || 0}</div>
          </div>
          <div className="bg-novatus-card-2 border border-novatus rounded-lg p-3 col-span-2">
            <div className="label-eyebrow">Ø Score</div>
            <div className="font-display font-black text-2xl text-novatus-green mt-1">{user.average_score || 0}</div>
          </div>
          {user.phone && (
            <div className="bg-novatus-card-2 border border-novatus rounded-lg p-3 col-span-2">
              <div className="label-eyebrow">Telefon</div>
              <div className="text-white mt-1 font-mono text-sm">{user.phone}</div>
            </div>
          )}
          {user.company && (
            <div className="bg-novatus-card-2 border border-novatus rounded-lg p-3 col-span-2">
              <div className="label-eyebrow">Firma</div>
              <div className="text-white mt-1 text-sm">{user.company}</div>
            </div>
          )}
        </div>

        {isSelf ? (
          <div className="bg-novatus-card-2 border border-novatus rounded-lg p-3 text-xs text-novatus-text-2">
            Du kannst dich nicht selbst löschen.
          </div>
        ) : isProtected ? (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-200 flex items-start gap-2">
            <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Dieser Account ist systemgeschützt und kann nicht gelöscht werden.</span>
          </div>
        ) : directorBlocked ? (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-200 flex items-start gap-2">
            <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Als Direktor kannst du keine anderen Direktoren oder Admins löschen.</span>
          </div>
        ) : (
          <div className="space-y-3 pt-3 border-t border-novatus">
            <button
              onClick={() => onSoftDelete(user)}
              className="w-full h-11 rounded-lg border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 font-semibold inline-flex items-center justify-center gap-2"
              data-testid="user-manage-soft-delete"
            >
              <Trash2 className="w-4 h-4" /> Anonymisieren (Soft-Delete)
            </button>
            <button
              onClick={() => onRequestHardDelete(user)}
              className="w-full h-11 rounded-lg border border-red-500/40 bg-red-500/10 hover:bg-red-500/20 text-red-400 font-semibold inline-flex items-center justify-center gap-2"
              data-testid="user-manage-hard-delete"
            >
              <AlertTriangle className="w-4 h-4" /> Endgültig löschen (Hard-Delete)
            </button>
            <p className="text-[11px] text-novatus-text-3 leading-relaxed">
              <strong className="text-novatus-text-2">Soft-Delete:</strong> Name & E-Mail werden anonymisiert,
              Login gesperrt, Trainings-Daten bleiben für Compliance erhalten. Reversibel.<br />
              <strong className="text-novatus-text-2">Hard-Delete:</strong> Vollständige Löschung inkl. aller
              Sessions, Nachrichten, Feedbacks und Audio-Aufnahmen. <strong className="text-red-400">Nicht reversibel.</strong>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
